import { sedrApi, type SedrStarterSetType } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';

export function showSedrLibrarySetDialog(options: {
  mode: 'create' | 'rename';
  setType?: SedrStarterSetType;
  initialName?: string;
  initialDescription?: string | null;
  setId?: string;
  onDone: (setId: string) => void | Promise<void>;
}): void {
  const setType = options.setType ?? 'Requirements';
  const isFolders = setType === 'Folders';
  const shell = createModalShell({
    titleKey: options.mode === 'create'
      ? (isFolders ? 'sedr.library.createFolderSetTitle' : 'sedr.library.createRequirementsSetTitle')
      : (isFolders ? 'sedr.library.renameFolderSetTitle' : 'sedr.library.renameRequirementsSetTitle'),
    titleId: 'sedr-library-set-dialog-title',
    helpTopic: dialogHelpTopic('sedrLibrary'),
  });

  shell.body.innerHTML = `
    <form class="form-grid" id="sedr-library-set-form">
      <label>
        <span data-i18n="sedr.library.nameLabel"></span>
        <input type="text" name="name" maxlength="200" required autocomplete="off" />
      </label>
      <label>
        <span data-i18n="sedr.library.descriptionLabel"></span>
        <textarea name="description" maxlength="2000" rows="3"></textarea>
      </label>
      <p class="error-text" id="sedr-library-set-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-library-set-form');
  submitButton.setAttribute(
    'data-i18n',
    options.mode === 'create'
      ? (isFolders ? 'sedr.library.createFolderSet' : 'sedr.library.createRequirementsSet')
      : (isFolders ? 'sedr.library.renameFolderSet' : 'sedr.library.renameRequirementsSet'),
  );

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-library-set-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-library-set-error') as HTMLParagraphElement;
  const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
  const descriptionInput = form.querySelector('textarea[name="description"]') as HTMLTextAreaElement;
  nameInput.value = options.initialName ?? '';
  descriptionInput.value = options.initialDescription ?? '';
  let submitting = false;
  let lastErrorKey = 'status.error';

  const refreshLocale = () => {
    if (!errorEl.hidden) {
      errorEl.textContent = t(lastErrorKey);
    }
    applyI18n(shell.dialog);
  };
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    if (submitting) return;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);
  nameInput.focus();
  nameInput.select();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitting) return;

    void (async () => {
      submitting = true;
      errorEl.hidden = true;
      submitButton.disabled = true;
      const data = new FormData(form);
      const name = String(data.get('name') ?? '').trim();
      const description = String(data.get('description') ?? '').trim() || null;
      try {
        if (options.mode === 'create') {
          const created = await sedrApi.createStarterSet({ name, description, setType });
          submitting = false;
          unbindLocale();
          unbindDismiss();
          shell.backdrop.remove();
          await options.onDone(created.id);
          return;
        }

        if (!options.setId) {
          throw new Error('missing_set_id');
        }
        await sedrApi.patchStarterSet(options.setId, { name, description });
        submitting = false;
        unbindLocale();
        unbindDismiss();
        shell.backdrop.remove();
        await options.onDone(options.setId);
      } catch (cause) {
        submitting = false;
        submitButton.disabled = false;
        lastErrorKey = sedrErrorMessageKey(cause);
        errorEl.hidden = false;
        errorEl.textContent = t(lastErrorKey);
      }
    })();
  });
}
